import React, { useState } from 'react';
import Image from 'next/image';
import {
  Avatar,
  Button,
  IconButton,
  InputLabel,
  TextareaAutosize,
  TextField,
} from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import LinearProgress from '@material-ui/core/LinearProgress';

import { emailRegex } from 'utils/helpers';
import { formatPrice } from 'utils/helpers';
import NoAvatarImage from 'public/images/no-avatar.svg';
import EditIcon from 'public/icons/edit-purple.svg';
import useCurrentUser from 'hooks/useCurrentUser';
import ReadMore from 'components/ReadMore';
import styles from 'styles/components/cart/Cart.module.scss';

const alertStyle = { width: 30, height: 30 };
const avatarFullSize = { width: '100%', height: '100%' };

const CustomLinearProgress = withStyles({
  root: {
    height: 5,
    borderRadius: 5,
  },

  colorPrimary: {
    backgroundColor: '#ebedf2',
  },

  bar: {
    borderRadius: 5,
    backgroundColor: '#5b00db',
  },
})(LinearProgress);

const marginTop = { marginTop: -18, minWidth: 50 };

const WishInfo = ({ wish, wishInfo, setWishInfo, userInfo, setUserInfo, profile }) => {
  const [amount, setAmount] = useState(wish.price);
  const [editMode, setEditMode] = useState(true);
  const currentUser = useCurrentUser();

  const MIN = 5;
  const MAX = +wish.price;

  const isCrowdfunding = wish.kind === 'crowdfunding';
  const emailError = userInfo.email && !userInfo.email.match(emailRegex);
  const amountError = amount && amount <= 5;
  const disabled = emailError || amountError || !userInfo.name || !userInfo.email || !amount;

  const emailErrorMsg = () => {
    if (emailError) return 'is not valid.';

    if (!userInfo.email) return 'is required.';
  };

  const amountErrorMsg = () => {
    if (amountError) return 'must be larger than 5.';

    if (amount > MAX) return 'must be less than the price of the wish.';

    if (!amount) return 'is required.';
  };

  const handleSave = () => {
    if (!amount) {
      setAmount(wish.price);
    } else {
      setWishInfo(prevState => ({ ...prevState, amount }));
    }

    setEditMode(false);
  };

  const normalise = value => ((value - MIN) * 100) / (MAX - MIN);

  return (
    <div className={styles.wishInfo}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
        <Avatar style={alertStyle} src={profile?.avatar_url} alt="Network">
          <NoAvatarImage style={avatarFullSize} />
        </Avatar>

        <h4 style={{ fontSize: 16, marginLeft: 10, wordBreak: 'break-word' }}>
          {isCrowdfunding
            ? `Contribute to ${profile?.display_name}'s ${wish.name}  `
            : `Buy a ${wish.name} for ${profile?.display_name}`}
        </h4>
      </div>

      <div className={styles.wishContainer}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className={styles.wishImage}>
              <Image width={50} height={50} src={wish?.image_url} alt={wish.name} unoptimized />
            </div>

            <h4 style={{ fontSize: 16, margin: '0px 10px', textAlign: 'left' }}>{wish?.name}</h4>
          </div>

          <div style={{ marginLeft: 'auto', textAlign: 'right', minWidth: 'fit-content' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h2
                className="color-main"
                style={{ fontSize: 16, marginRight: 5, lineHeight: 1.67, letterSpacing: 0.41 }}
              >
                {formatPrice(amount)}
              </h2>

              {isCrowdfunding && (
                <span style={{ color: '#323643' }}> out of {formatPrice(wish.price)}</span>
              )}
            </div>

            {!editMode && isCrowdfunding && (
              <CustomLinearProgress variant="determinate" value={normalise(amount)} />
            )}
          </div>
        </div>

        {editMode ? (
          <div className={styles.form}>
            {isCrowdfunding && (
              <div>
                <InputLabel>Add your contribution</InputLabel>

                <TextField
                  type="number"
                  defaultValue={amount}
                  onChange={e => setAmount(e.target.value)}
                  variant="outlined"
                  placeholder="Add price"
                  error={amountError || !amount || amount > MAX}
                  helperText={amountErrorMsg()}
                />
              </div>
            )}

            {!currentUser.name && (
              <>
                <InputLabel>Name</InputLabel>
                <TextField
                  text="text"
                  defaultValue={userInfo.name}
                  onChange={e =>
                    setUserInfo(prevState => ({
                      ...prevState,
                      name: e.target.value,
                    }))
                  }
                  variant="outlined"
                  placeholder="Name"
                  error={!userInfo.name}
                  helperText={!userInfo.name && 'is required.'}
                />
              </>
            )}

            {!currentUser.email && (
              <>
                <InputLabel>Email</InputLabel>

                <TextField
                  type="email"
                  name="email"
                  value={userInfo.email}
                  placeholder="Email"
                  onChange={e =>
                    setUserInfo(prevState => ({
                      ...prevState,
                      email: e.target.value,
                    }))
                  }
                  variant="outlined"
                  error={emailError || !userInfo.email}
                  helperText={emailErrorMsg()}
                />
              </>
            )}

            <InputLabel>Message</InputLabel>
            <TextareaAutosize
              onChange={e =>
                setWishInfo(prevState => ({
                  ...prevState,
                  message: e.target.value,
                }))
              }
              defaultValue={wishInfo.message}
              minRows={4}
              maxRows={4}
              placeholder="Add message"
            />
            <div style={{ width: '100%', marginTop: 20, textAlign: 'center' }}>
              <Button
                className={styles.button}
                onClick={handleSave}
                color="primary"
                variant="contained"
                disabled={disabled}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.formPreview}>
            {isCrowdfunding && (
              <div
                className={styles.row}
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <div>
                  <InputLabel>Your contribution</InputLabel>

                  <div className={styles.contribution}>{formatPrice(amount)}</div>
                </div>

                <IconButton onClick={() => setEditMode(true)} style={marginTop}>
                  <EditIcon />
                </IconButton>
              </div>
            )}

            <div
              className={styles.row}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <div>
                <InputLabel>Name</InputLabel>

                <div>{userInfo.name}</div>
              </div>

              {!isCrowdfunding && (
                <IconButton onClick={() => setEditMode(true)} style={marginTop}>
                  <EditIcon />
                </IconButton>
              )}
            </div>

            <div className={styles.row}>
              <InputLabel>Email</InputLabel>

              <div>{userInfo.email}</div>
            </div>

            <div className={styles.row}>
              <InputLabel>Message</InputLabel>

              <ReadMore description={wishInfo.message} mobileLength={150} desktopLength={150} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishInfo;
